import EastIcon from '@mui/icons-material/East';
import {
  Box,
  colors,
  Stack,
  Typography,
  styled,
  type BoxProps,
  type TextFieldProps,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { Button } from '~/components/Button';
import { ReportsDropdown } from '~/components/ReportsDropdown';
import { useCanonicalSearch } from '~/hooks/useCanonicalSearch';
import useRouter from '~/hooks/useRouter';
import { useSuggestions } from '~/hooks/useSuggestions';
import { isEmptySearch } from '~/scripts/isEmptySearch';
import { isSingleReportSearch } from '~/scripts/isSingleReportSearch';
import { type Searches } from '~/types/searches';

const allDomainsCount = 14720877;

function useReportTotal(search: Searches.SearchObject): number | null {
  const suggestions = useSuggestions();
  const canonicalSearch = useCanonicalSearch(search || {});
  if (isSingleReportSearch(canonicalSearch)) {
    const uuid = search.all[0]?.value;
    const report = suggestions.find((suggestion) => suggestion.uuid === uuid);
    return report?.total || allDomainsCount || null;
  }

  return null;
}

function useExcludedReports(search: Searches.SearchObject | null): string[] {
  const excludedReports = useMemo(() => {
    if (!search) {
      return [];
    }
    return [
      ...search.none.map(
        (item) => (item.type === 'report' && item.value) || ''
      ),
      ...search.all.map((item) => (item.type === 'report' && item.value) || ''),
      ...search.any.map((item) => (item.type === 'report' && item.value) || ''),
    ].filter(Boolean) as string[];
  }, [search]);
  return excludedReports;
}

interface SearchFormProps extends BoxProps {
  search: Searches.SearchObject | null;
  setSearch: Dispatch<SetStateAction<Searches.SearchObject>>;
  isLoading: boolean;
  TextFieldProps?: TextFieldProps;
}
export const SearchForm = ({
  search,
  setSearch,
  isLoading,
  TextFieldProps,
  ...boxProps
}: SearchFormProps) => {
  const placeholder = 'Add a technology, HTML, or JS snippet';

  const excludedReports = useExcludedReports(search);
  return (
    <Box {...boxProps}>
      <Stack direction="column" spacing={1}>
        <QueryRow>
          <QueryRowHead>
            <OperatorBadge className="and">AND</OperatorBadge>
            <QueryRowLabel>All of these</QueryRowLabel>
            <QueryRowHelper>match every technology</QueryRowHelper>
          </QueryRowHead>
          <ReportsDropdown
            value={search?.all || []}
            operator="and"
            disabled={isLoading}
            excludedReports={excludedReports}
            placeholder={search?.all.length ? undefined : placeholder}
            onChange={(items) =>
              setSearch((search) => ({
                ...(search || { any: [], none: [] }),
                all: items,
              }))
            }
            TextFieldProps={TextFieldProps}
          />
        </QueryRow>

        <QueryRow>
          <QueryRowHead>
            <OperatorBadge className="or">OR</OperatorBadge>
            <QueryRowLabel>Any of these</QueryRowLabel>
            <QueryRowHelper>match at least one</QueryRowHelper>
          </QueryRowHead>
          <ReportsDropdown
            value={search?.any || []}
            operator="or"
            disabled={isLoading}
            excludedReports={excludedReports}
            placeholder={search?.any.length ? undefined : placeholder}
            onChange={(items) =>
              setSearch((search) => ({
                ...(search || { all: [], none: [] }),
                any: items,
              }))
            }
            TextFieldProps={TextFieldProps}
          />
        </QueryRow>

        <QueryRow>
          <QueryRowHead>
            <OperatorBadge className="not">NOT</OperatorBadge>
            <QueryRowLabel>None of these</QueryRowLabel>
            <QueryRowHelper>exclude these technologies</QueryRowHelper>
          </QueryRowHead>
          <ReportsDropdown
            value={search?.none || []}
            operator="and not"
            disabled={isLoading}
            excludedReports={excludedReports}
            placeholder={search?.none.length ? undefined : placeholder}
            onChange={(items) =>
              setSearch((search) => ({
                ...(search || { all: [], any: [] }),
                none: items,
              }))
            }
            TextFieldProps={TextFieldProps}
          />
        </QueryRow>
      </Stack>
    </Box>
  );
};

export const SearchFormCard = () => {
  const [search, setSearch] = useState<Searches.SearchObject>({
    all: [],
    any: [],
    none: [],
  });
  const [matchCount, setMatchCount] = useState(allDomainsCount);
  const countRef = useRef(matchCount);
  const reportTotal = useReportTotal(search);
  const canonicalSearch = useCanonicalSearch(search);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const emptySearch = useMemo(() => {
    return isEmptySearch(canonicalSearch);
  }, [canonicalSearch]);

  const href = useMemo(() => {
    if (emptySearch) {
      return '';
    }
    return `/reports/new?search=${JSON.stringify(canonicalSearch)}`;
  }, [canonicalSearch, emptySearch]);

  useEffect(() => {
    if (!href) {
      return;
    }
    setIsLoading(true);
    router.push(href);
  }, [router, href, setIsLoading]);

  useEffect(() => {
    const updatedTotal = reportTotal || allDomainsCount;
    const maxDuration = 4000;
    const interval = 40;
    const maxTicks = maxDuration / interval;
    let tick = 0;

    const intervalId = setInterval(() => {
      const diff = updatedTotal - countRef.current;
      if (Math.abs(diff) < 5) {
        countRef.current = updatedTotal;
        setMatchCount(updatedTotal);
        clearInterval(intervalId);
        return;
      }
      // Ease-in: fraction grows from ~1/16 to ~1/2 over maxTicks
      const progress = Math.min(tick / maxTicks, 1);
      const fraction = 0.0625 + progress * 0.4375;
      tick++;
      countRef.current +=
        Math.sign(diff) * Math.max(1, Math.floor(Math.abs(diff) * fraction));
      setMatchCount(countRef.current);
    }, interval);
    return () => clearInterval(intervalId);
  }, [reportTotal]);
  return (
    <QueryBuilderCardRoot>
      <QueryBuilderHead>
        <QueryBuilderLabel>Try a query</QueryBuilderLabel>
        <QueryTabGroup></QueryTabGroup>
      </QueryBuilderHead>

      <SearchForm
        search={search}
        setSearch={setSearch}
        isLoading={isLoading}
        TextFieldProps={{ size: 'small' }}
      />

      <QueryBuilderFoot>
        <LiveCount>
          <LiveCountNumber>
            {(matchCount || allDomainsCount).toLocaleString('en-US')}
          </LiveCountNumber>
          <LiveCountLabel>
            {matchCount && matchCount === allDomainsCount
              ? 'total domains'
              : 'matching domains'}
          </LiveCountLabel>
          <LiveCountPulse />
        </LiveCount>
        <Stack direction="row" spacing={1}>
          <Button
            href={href}
            variant="contained"
            disabled={emptySearch || isLoading}
            endIcon={<EastIcon fontSize="small" />}>
            Run search
          </Button>
        </Stack>
      </QueryBuilderFoot>
    </QueryBuilderCardRoot>
  );
};

const QueryBuilderCardRoot = styled(Paper)`
  text-align: left;
  border: 1px solid ${colors.grey[200]};
  border-radius: 20px;
  box-shadow:
    0 12px 24px -8px rgba(15, 23, 42, 0.1),
    0 4px 8px -4px rgba(15, 23, 42, 0.05);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const QueryBuilderHead = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 8px;
`;

const QueryBuilderLabel = styled(Typography)`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.grey[500]};
` as typeof Typography;

const QueryTabGroup = styled(Box)`
  display: flex;
  gap: 2px;
  background: ${colors.grey[100]};
  padding: 3px;
  border-radius: 999px;
`;

const QueryRow = styled(Box)`
  padding: 10px 12px;
  border-radius: 10px;
  transition: background 0.15s;

  &:hover {
    background: ${colors.grey[50]};
  }
`;

const QueryRowHead = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const OperatorBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;

  &.and {
    background: ${colors.green[50]};
    color: ${colors.green[800]};
  }
  &.or {
    background: ${colors.blue[50]};
    color: ${colors.blue[800]};
  }
  &.not {
    background: ${colors.red[50]};
    color: ${colors.red[700]};
  }
`;

const QueryRowLabel = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.grey[900]};
` as typeof Typography;

const QueryRowHelper = styled(Typography)`
  font-size: 13px;
  color: ${colors.grey[700]};
` as typeof Typography;

const QueryBuilderFoot = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  margin-top: 4px;
  border-top: 1px solid ${colors.grey[100]};
  flex-wrap: wrap;
  gap: 12px;
`;

const LiveCount = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const LiveCountNumber = styled(Typography)`
  font-feature-settings: 'tnum';
  font-size: 22px;
  font-weight: 600;
  color: ${colors.grey[900]};
  letter-spacing: -0.01em;
` as typeof Typography;

const LiveCountLabel = styled(Typography)`
  font-size: 14px;
  color: ${colors.grey[600]};
` as typeof Typography;

const LiveCountPulse = styled(Box)`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${colors.green[500]};
  margin-left: 4px;
  align-self: center;

  @keyframes countPulse {
    0%,
    100% {
      opacity: 1;
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.5);
    }
    50% {
      opacity: 0.55;
      box-shadow: 0 0 0 6px transparent;
    }
  }

  animation: countPulse 1.6s ease-in-out infinite;
`;
